import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquareQuote } from "lucide-react";
import QuestionBadge from "./QuestionBadge";
import { QuestionBlockSkeleton } from "./QuestionBlockStates";
import TiltCard from "./TiltCard";
import { supabase } from "@/integrations/supabase/client";
import { usePrefetchOnIntent } from "@/hooks/usePrefetchOnIntent";


type AnswerListItem = {
  id: string;
  slug: string;
  search_query: string;
  episode_count: number;
  theme: string | null;
  created_at: string;
};

// Module-level cache. list_public_answers changes a few times per day at most,
// so a 10-minute TTL eliminates the per-mount RPC on every homepage visit and
// back-navigation while still picking up new answers within the same session.
const TTL_MS = 10 * 60_000;
let featuredCache: { items: AnswerListItem[]; at: number } | null = null;
let inflight: Promise<AnswerListItem[]> | null = null;

async function loadFeaturedAnswers(): Promise<AnswerListItem[]> {
  const now = Date.now();
  if (featuredCache && now - featuredCache.at < TTL_MS) return featuredCache.items;
  if (inflight) return inflight;
  inflight = (async () => {
    const { data } = await supabase.rpc("list_public_answers", { _limit: 4, _offset: 0 });
    const items = (data as AnswerListItem[]) ?? [];
    featuredCache = { items, at: Date.now() };
    inflight = null;
    return items;
  })();
  return inflight;
}

export default function FeaturedAnswers() {
  // Prime synchronously from cache so back-nav paints instantly with zero flicker.
  const cachedInitial = featuredCache && Date.now() - featuredCache.at < TTL_MS ? featuredCache.items : null;
  const [items, setItems] = useState<AnswerListItem[]>(cachedInitial ?? []);
  const [loading, setLoading] = useState(!cachedInitial);
  const prefetchAnswer = usePrefetchOnIntent(() => import("@/pages/AnswerPage"));

  useEffect(() => {
    let cancelled = false;
    loadFeaturedAnswers().then((data) => {
      if (cancelled) return;
      setItems(data);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section className="w-full max-w-2xl mx-auto mt-6">
        <QuestionBlockSkeleton rows={4} label="Answered by the pods" />
      </section>
    );
  }

  if (items.length === 0) return null;



  return (
    <section className="w-full max-w-2xl mx-auto mt-6">
      <TiltCard className="hover-lift transition-all duration-200">
        <div
          className={[
            "relative overflow-hidden rounded-md p-4 sm:p-5 pl-5 sm:pl-7",
            // Same peach/orange tint + dashed primary border as AnsweredQuestionCallout
            "bg-[hsl(var(--primary)/0.08)]",
            "border-2 border-dashed border-primary/60",
            "shadow-[4px_4px_0_0_hsl(var(--primary)/0.35)] hover:shadow-[6px_6px_0_0_hsl(var(--primary)/0.5)]",
            "transition-all",
          ].join(" ")}
        >

          {/* Left accent stripe — matches the callout */}
          <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1.5 bg-primary" />

          {/* Decorative quote mark — same "written answer" cue */}
          <MessageSquareQuote
            aria-hidden
            className="absolute -top-2 -right-2 w-20 h-20 text-primary/10 rotate-12 pointer-events-none"
          />

          <div className="flex items-center justify-between gap-2 mb-3 relative">
            <QuestionBadge label="Answered by the pods" icon="sparkles" />
            <Link
              to="/answers"
              className="font-mono text-[10px] uppercase tracking-widest text-primary hover:underline inline-flex items-center gap-0.5 font-bold"
            >
              All answers <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          <ul className="space-y-1 relative">
            {items.map((i) => (
              <li key={i.id}>
                <Link
                  to={`/answer/${i.slug}`}
                  {...prefetchAnswer}
                  className="group flex items-baseline gap-2 py-1.5 px-2 -mx-2 rounded hover:bg-[hsl(var(--primary)/0.1)] transition-colors"
                >
                  <MessageSquareQuote
                    aria-hidden
                    className="w-3.5 h-3.5 shrink-0 text-primary/70 translate-y-0.5"
                  />
                  <span className="question-title text-base sm:text-lg line-clamp-1 group-hover:text-primary transition-colors">
                    {i.search_query}
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground shrink-0 ml-auto whitespace-nowrap">
                    {i.episode_count} ep{i.episode_count === 1 ? "" : "s"}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </TiltCard>
    </section>
  );
}
